import React from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const Shipping = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-black py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 text-center">
            Shipping & Returns
          </h1>
          <p className="text-light max-w-3xl mx-auto text-center text-lg">
            Information about our shipping policies, delivery times, and return procedures.
          </p>
        </div>
      </div>
      
      {/* Shipping Information */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl font-bold text-primary mb-8">
              Shipping Information
            </h2>
            
            <div className="space-y-8">
              <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Shipping Methods & Timeframes
                </h3>
                
                <p className="text-light mb-4">
                  At Kala Hats, we ship our handcrafted hats worldwide. Each hat is carefully packaged to ensure it arrives in perfect condition. Our standard shipping box dimensions are 40x40x17 cm.
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="py-3 px-4 text-primary">Region</th>
                        <th className="py-3 px-4 text-primary">Standard Shipping</th>
                        <th className="py-3 px-4 text-primary">Express Shipping</th>
                        <th className="py-3 px-4 text-primary">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr className="border-b border-gray-700">
                        <td className="py-3 px-4">Indonesia</td>
                        <td className="py-3 px-4">2-3 business days</td>
                        <td className="py-3 px-4">1 business day</td>
                        <td className="py-3 px-4">Rp 75.000 - Rp 225.000</td>
                      </tr>
                      <tr className="border-b border-gray-700">
                        <td className="py-3 px-4">Southeast Asia</td>
                        <td className="py-3 px-4">5-7 business days</td>
                        <td className="py-3 px-4">2-3 business days</td>
                        <td className="py-3 px-4">Rp 225.000 - Rp 375.000</td>
                      </tr>
                      <tr className="border-b border-gray-700">
                        <td className="py-3 px-4">Australia & New Zealand</td>
                        <td className="py-3 px-4">7-10 business days</td>
                        <td className="py-3 px-4">3-5 business days</td>
                        <td className="py-3 px-4">Rp 300.000 - Rp 450.000</td>
                      </tr>
                      <tr className="border-b border-gray-700">
                        <td className="py-3 px-4">Europe</td>
                        <td className="py-3 px-4">10-14 business days</td>
                        <td className="py-3 px-4">5-7 business days</td>
                        <td className="py-3 px-4">Rp 375.000 - Rp 600.000</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">North America</td>
                        <td className="py-3 px-4">10-14 business days</td>
                        <td className="py-3 px-4">5-7 business days</td>
                        <td className="py-3 px-4">Rp 375.000 - Rp 600.000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <p className="text-gray-400 mt-4 text-sm">
                  * Shipping times are estimates and may vary depending on customs processing and local delivery conditions. Rates are shown in IDR.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Shipping for Custom Orders
                </h3>
                
                <p className="text-light mb-4">
                  For custom hat orders, shipping timeframes begin once your hat is completed. The production time for custom hats varies depending on complexity:
                </p>
                
                <ul className="list-disc pl-6 mb-4 text-light space-y-2">
                  <li>Simple designs: 1-2 weeks production time</li>
                  <li>Complex designs: 3-4 weeks production time</li>
                  <li>Highly detailed or specialized designs: 4-6 weeks production time</li>
                </ul>
                
                <p className="text-light">
                  We'll provide you with a production timeline estimate when you place your custom order, and we'll keep you updated on the progress of your hat.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Tracking Your Order
                </h3>
                
                <p className="text-light mb-4">
                  Once your order ships, you'll receive a confirmation email with tracking information. You can use this tracking number to monitor your package's journey to you.
                </p>
                
                <p className="text-light">
                  If you have any questions about your shipment or experience any issues with tracking, please don't hesitate to contact us at <a href="mailto:info@kalahats.com" className="text-primary hover:underline">info@kalahats.com</a> or via WhatsApp at <a href="https://wa.me/6281289098912" className="text-primary hover:underline">+62 812 8909 8912</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Returns & Exchanges */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl font-bold text-primary mb-8">
              Returns & Exchanges
            </h2>
            
            <div className="space-y-8">
              <div className="bg-secondary p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Ready-Made Hat Returns
                </h3>
                
                <p className="text-light mb-4">
                  We want you to be completely satisfied with your Kala hat. If you're not happy with your purchase, we accept returns of unworn, unaltered ready-made hats within 14 days of delivery.
                </p>
                
                <p className="text-light mb-4">
                  To initiate a return:
                </p>
                
                <ol className="list-decimal pl-6 mb-4 text-light space-y-2">
                  <li>Contact us at <a href="mailto:returns@kalahats.com" className="text-primary hover:underline">returns@kalahats.com</a> with your order number and reason for return</li>
                  <li>We'll provide you with return instructions and a return authorization</li>
                  <li>Package the hat carefully in its original packaging if possible</li>
                  <li>Ship the hat back to our workshop in Bali (customer is responsible for return shipping costs)</li>
                  <li>Once we receive and inspect the returned hat, we'll process your refund within 5-7 business days</li>
                </ol>
                
                <p className="text-gray-400 text-sm">
                  * Please note that shipping costs are non-refundable, and a 15% restocking fee may apply.
                </p>
              </div>
              
              <div className="bg-secondary p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Custom Hat Policy
                </h3>
                
                <p className="text-light mb-4">
                  Due to the personalized nature of custom hats, they are not eligible for returns or exchanges unless there is a manufacturing defect. We take great care to ensure your custom hat meets the specifications we've agreed upon.
                </p>
                
                <p className="text-light mb-4">
                  If you receive a custom hat that doesn't match the agreed-upon design or has a manufacturing defect:
                </p>
                
                <ol className="list-decimal pl-6 mb-4 text-light space-y-2">
                  <li>Contact us within 7 days of receiving your hat</li>
                  <li>Provide clear photos showing the issue</li>
                  <li>We'll work with you to resolve the issue, which may include repairs, adjustments, or in rare cases, remaking the hat</li>
                </ol>
                
                <p className="text-light">
                  Our goal is your complete satisfaction, and we stand behind the quality of our craftsmanship.
                </p>
              </div>
              
              <div className="bg-secondary p-6 rounded-lg border border-gray-700">
                <h3 className="font-serif text-xl font-bold text-primary mb-4">
                  Exchanges
                </h3>
                
                <p className="text-light mb-4">
                  If you need a different size or prefer a different style of ready-made hat, we're happy to facilitate an exchange. Please follow the same process as returns, but indicate that you'd like an exchange instead of a refund.
                </p>
                
                <p className="text-light">
                  Please note that exchanges are subject to product availability, and you may be responsible for additional shipping costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Hat Care */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl font-bold text-primary mb-8">
              Hat Care Instructions
            </h2>
            
            <div className="bg-black/30 p-6 rounded-lg border border-gray-700">
              <p className="text-light mb-6">
                To ensure your Kala hat maintains its beauty and shape for years to come, we recommend the following care instructions:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-serif text-xl font-bold text-primary mb-3">
                    Straw & Woven Hats
                  </h3>
                  
                  <ul className="list-disc pl-6 text-light space-y-2">
                    <li>Store in a cool, dry place away from direct sunlight</li>
                    <li>Use a soft brush to remove dust and dirt</li>
                    <li>Avoid getting your hat wet; if it does get wet, let it dry naturally (never use heat)</li>
                    <li>Handle by the brim to avoid misshaping the crown</li>
                    <li>For storage, place the hat upside down on its crown or use a hat stand</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-serif text-xl font-bold text-primary mb-3">
                    Felt & Fabric Hats
                  </h3>
                  
                  <ul className="list-disc pl-6 text-light space-y-2">
                    <li>Use a hat brush to remove dust and debris, brushing in a counterclockwise direction</li>
                    <li>For water spots, allow to dry completely then gently steam and brush</li>
                    <li>Store in a hatbox or on a hat stand to maintain shape</li>
                    <li>Avoid handling with oily hands</li>
                    <li>For stubborn stains, consult a professional hat cleaner</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-light mt-6">
                Each Kala hat comes with specific care instructions tailored to its materials and construction. Following these guidelines will help ensure your hat remains beautiful for years to come.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact CTA */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-primary mb-6">
            Questions About Shipping or Returns?
          </h2>
          <p className="text-light max-w-2xl mx-auto mb-8">
            Our team is here to help. If you have any questions about shipping, returns, or hat care, please don't hesitate to reach out.
          </p>
          <Button to="/contact" variant="primary" size="lg">
            Contact Us
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Shipping;
